/**
 * Consent: the step Cognito cannot do.
 *
 * An MCP client (Claude, ChatGPT, Cursor, …) sent the user here through the OAuth facade. Cognito has
 * already established *who* they are; this page establishes *what they agree to* and, just as
 * importantly, *which subscription pays for the runs the agent will start*. Claude cannot send the
 * subscription header the API would otherwise use, so this choice has to be made by a human, here,
 * and is then bound into the token that gets issued.
 *
 * Two details are deliberate rather than cosmetic:
 *
 *  - The app is identified by the **host** of its metadata document, never by the name it chose for
 *    itself. The document is self-asserted; the host is where it actually lives. Showing a
 *    `client_name` as the identity is how a consent screen gets used to phish.
 *  - A loopback redirect gets an explicit warning. Any process on the user's machine can bind a port
 *    and claim to be the legitimate desktop client, and the MCP specification asks authorization
 *    servers to say so.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {Auth} from 'aws-amplify'
import {getUserSubscriptions, MembershipSubscription} from '../modules/membership/api'
import {
  approveConsent,
  denyConsent,
  getConsentDetails,
  SCOPE_LABELS,
  type ConsentDetails,
} from '../modules/connect/api'
import {getLogger} from '../../shared/utils/logUtils'

type PageState = 'loading' | 'signing-in' | 'ready' | 'submitting' | 'redirecting' | 'error'

/** Where AppRoutes looks for a post-login destination. Shared with the deep-link mechanism there. */
const RETURN_TO_KEY = 'ob_return_to'

/**
 * Sign in without asking for a password again.
 *
 * The visitor reached this page one redirect after authenticating at the Cognito hosted UI, so its
 * session cookie is fresh: bouncing them back through the hosted UI returns immediately with a code
 * and no prompt. Reaching for the app's own SRP login form instead is what made people type their
 * password twice in a row.
 */
async function signInSilently(returnTo: string): Promise<void> {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, returnTo)
  } catch {
    /* private mode — the worst case is landing on the dashboard afterwards */
  }
  await Auth.federatedSignIn()
}

/** Subscriptions that cannot pay for a run are not offered as if they could. */
const isUsable = (sub: MembershipSubscription): boolean => !sub.is_expired && sub.is_enabled !== false

const subscriptionLabel = (sub: MembershipSubscription): string => {
  const product = sub.product || 'OmicsBox'
  const flags = [
    sub.is_evaluation ? 'trial' : null,
    sub.is_primary ? 'current' : null,
    sub.end_date ? `until ${sub.end_date}` : null,
  ].filter(Boolean)
  return `${product} — ${sub.subscription_id}${flags.length ? ` (${flags.join(', ')})` : ''}`
}

export const ConnectAuthorizePage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const requestId = searchParams.get('request') ?? ''

  const [state, setState] = useState<PageState>('loading')
  const [error, setError] = useState('')
  const [details, setDetails] = useState<ConsentDetails | null>(null)
  const [subscriptions, setSubscriptions] = useState<MembershipSubscription[]>([])
  const [chosen, setChosen] = useState('')

  useEffect(() => {
    document.body.classList.add('signInPage')
    return () => {
      document.body.classList.remove('signInPage')
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      // The facade reports a failed sign-in by sending the browser back here with a reason, rather
      // than leaving raw JSON in the address bar.
      const failure = searchParams.get('error')
      if (failure) {
        setError(
          failure === 'access_denied'
            ? 'Sign-in was cancelled. Start the connection again from your app.'
            : 'Sign-in could not be completed. Start the connection again from your app.'
        )
        setState('error')
        return
      }
      if (!requestId) {
        setError('This link is missing its request id. Start the connection again from your app.')
        setState('error')
        return
      }
      // This page is reachable signed-out (see AppRoutes): it authenticates itself so the visitor
      // does not meet a second login form seconds after the first one.
      try {
        await Auth.currentSession()
      } catch {
        if (cancelled) return
        setState('signing-in')
        await signInSilently(`/connect/authorize?request=${encodeURIComponent(requestId)}`)
        return
      }

      try {
        // Both in parallel: neither depends on the other, and this page sits in the middle of a
        // redirect chain where every extra round trip is felt.
        const [consent, subs] = await Promise.all([
          getConsentDetails(requestId),
          getUserSubscriptions().catch((err) => {
            getLogger().error('Could not load subscriptions for consent', err)
            return {subscriptions: []}
          }),
        ])
        if (cancelled) return
        setDetails(consent)
        const usable = subs.subscriptions.filter(isUsable)
        setSubscriptions(usable)
        setChosen(usable.find((s) => s.is_primary)?.subscription_id ?? usable[0]?.subscription_id ?? '')
        setState('ready')
      } catch (err: any) {
        if (cancelled) return
        // The commonest cause by far is a request that expired while the user was signing in.
        setError(err?.message || 'This authorization request is no longer valid. Please start again.')
        setState('error')
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [requestId, searchParams])

  /** Hand the browser back to the client, whatever the decision was. */
  const leaveTo = useCallback((url: string) => {
    setState('redirecting')
    window.location.assign(url)
  }, [])

  const onApprove = async () => {
    if (!chosen) return
    setState('submitting')
    try {
      leaveTo((await approveConsent(requestId, chosen)).redirectUrl)
    } catch (err: any) {
      setError(err?.message || 'Could not complete the authorization.')
      setState('error')
    }
  }

  const onDeny = async () => {
    setState('submitting')
    try {
      leaveTo((await denyConsent(requestId)).redirectUrl)
    } catch (err: any) {
      // Denial failing is not worth an error screen — the request expires on its own either way.
      getLogger().warn('Deny failed; the request will expire', err)
      setError('Connection cancelled. You can close this tab.')
      setState('error')
    }
  }

  const scopeRows = useMemo(
    () =>
      (details?.scopes ?? []).map((scope) => ({
        scope,
        ...(SCOPE_LABELS[scope] ?? {title: scope, detail: ''}),
      })),
    [details]
  )

  if (state === 'loading' || state === 'signing-in') {
    return (
      <div className='d-flex flex-column justify-content-center align-items-center min-vh-100'>
        <span className='spinner-border text-primary' role='status' aria-label='Loading' />
        {state === 'signing-in' && <p className='text-gray-600 mt-4 mb-0'>Signing you in…</p>}
      </div>
    )
  }

  if (state === 'error' || !details) {
    return (
      <div className='d-flex justify-content-center align-items-center min-vh-100'>
        <div className='card shadow-sm' style={{maxWidth: 520}}>
          <div className='card-body p-8 text-center'>
            <h2 className='fs-3 fw-bold mb-4'>Connection not completed</h2>
            <p className='text-gray-600 mb-0'>{error}</p>
          </div>
        </div>
      </div>
    )
  }

  const busy = state === 'submitting' || state === 'redirecting'

  return (
    <div className='d-flex justify-content-center align-items-center min-vh-100 py-10'>
      <div className='card shadow-sm' style={{maxWidth: 600, width: '100%'}}>
        <div className='card-body p-8'>
          <h1 className='fs-2 fw-bold mb-2'>
            Connect <span className='text-primary'>{details.clientHost}</span> to OmicsBox
          </h1>
          <p className='text-gray-600 mb-6'>
            {details.email ? <>Signed in as {details.email}. </> : null}
            This app is asking to use OmicsCloud on your behalf.
          </p>

          <div className='mb-6'>
            <div className='fw-semibold mb-3'>It will be able to:</div>
            <ul className='list-unstyled mb-0'>
              {scopeRows.map((row) => (
                <li key={row.scope} className='d-flex mb-3'>
                  <i className='fas fa-check text-success mt-1 me-3' />
                  <div>
                    <div className='fw-semibold'>{row.title}</div>
                    {row.detail ? <div className='text-gray-600 fs-7'>{row.detail}</div> : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className='mb-6'>
            <label className='form-label fw-semibold' htmlFor='consent-subscription'>
              Runs will be billed to
            </label>
            {subscriptions.length === 0 ? (
              <div className='alert alert-warning mb-0'>
                No active subscription is available on this account, so analyses cannot be run yet. You
                can still connect, and choose a subscription later from Settings.
              </div>
            ) : (
              <>
                <select
                  id='consent-subscription'
                  className='form-select'
                  value={chosen}
                  disabled={busy}
                  onChange={(e) => setChosen(e.target.value)}
                >
                  {subscriptions.map((sub) => (
                    <option key={sub.subscription_id} value={sub.subscription_id}>
                      {subscriptionLabel(sub)}
                    </option>
                  ))}
                </select>
                <div className='form-text'>
                  CloudUnits consumed by analyses this app starts come out of this subscription.
                </div>
              </>
            )}
          </div>

          {details.loopbackRedirect ? (
            <div className='alert alert-light-warning d-flex mb-6'>
              <i className='fas fa-triangle-exclamation mt-1 me-3' />
              <div className='fs-7'>
                This app runs on your own computer and will receive the authorization on a local port.
                Only continue if you started this from an application you installed yourself.
              </div>
            </div>
          ) : null}

          <div className='d-flex gap-3'>
            <button
              type='button'
              className='btn btn-primary flex-grow-1'
              onClick={onApprove}
              disabled={busy || !chosen}
            >
              {busy ? 'Connecting…' : 'Allow access'}
            </button>
            <button type='button' className='btn btn-light' onClick={onDeny} disabled={busy}>
              Cancel
            </button>
          </div>

          <p className='text-gray-500 fs-8 mt-6 mb-0'>
            You can revoke this access at any time from Settings. Access is granted to{' '}
            <code>{details.clientHost}</code>, identified by where its application details are
            published — not by the name it reports.
          </p>
        </div>
      </div>
    </div>
  )
}

export default ConnectAuthorizePage
